import React, { useState, useEffect } from 'react';
import cvAnalysisService from '../../../services/ai/core/CVAnalysisService';
import { supabase } from '../../../services/supabaseClient';
import ProviderConfigModal from './ProviderConfigModal';
import AddProviderModal from './AddProviderModal';

/**
 * AI Model Management Component
 * Admin panel for configuring AI providers and models
 */
const AIModelManagement = () => {
  const [providers, setProviders] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('providers');
  const [configurations, setConfigurations] = useState([]);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      console.log('[Admin Panel] Loading data...');

      // Load provider status - this is now async
      const healthyProviders = await cvAnalysisService.getAvailableProviders();
      console.log('[Admin Panel] Loaded providers:', healthyProviders.length);
      setProviders(healthyProviders);

      // Load statistics
      const stats = cvAnalysisService.getStatistics();
      setStatistics(stats);

      // Load configurations from Supabase
      const { data: configs, error } = await supabase
        .from('ai_provider_config')
        .select('*')
        .order('priority', { ascending: true });

      if (!error && configs) {
        console.log('[Admin Panel] Loaded configurations:', configs.length);
        setConfigurations(configs);
      } else if (error) {
        console.error('[Admin Panel] Error loading configs:', error);
      }
    } catch (error) {
      console.error('Error loading AI model data:', error);
      showMessage('Error loading data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async (config) => {
    try {
      setSaving(true);

      console.log('[Admin Panel] Saving configuration:', config);

      const { error } = await supabase
        .from('ai_provider_config')
        .upsert({
          ...config,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'provider_name'
        });

      if (error) {
        console.error('[Admin Panel] Supabase error:', error);
        throw error;
      }

      console.log('[Admin Panel] Configuration saved successfully');
      showMessage('Configuration saved successfully', 'success');

      // Clear AI provider factory cache to reload configurations
      cvAnalysisService.clearCache();

      await loadData();
    } catch (error) {
      console.error('Error saving configuration:', error);
      showMessage('Error saving configuration: ' + (error.message || error.toString()), 'error');
    } finally {
      setSaving(false);
    }
  };

  const testProvider = async (providerName) => {
    try {
      const provider = providers.find(p => p.name === providerName);
      if (!provider) {
        showMessage('Provider not found', 'error');
        return;
      }

      showMessage(`Testing ${provider.displayName}...`, 'info');

      const testCV = `John Doe
Software Engineer
Email: john.doe@email.com
Phone: +1234567890

Education:
- Master of Science in Computer Science
  Stanford University, 2020
  GPA: 3.9/4.0

Experience:
- Senior Software Engineer at Google (2020-2024)
  Led development of cloud infrastructure using Kubernetes and Docker
  Built scalable microservices architecture serving 10M+ users
  Technologies: Python, Go, Kubernetes, AWS, Docker

- Software Engineer at StartupXYZ (2018-2020)
  Developed full-stack web applications
  Implemented CI/CD pipelines
  Technologies: React, Node.js, PostgreSQL, Docker

Skills:
- Programming: Python, JavaScript, Go, Java, TypeScript
- Cloud: AWS, Google Cloud, Azure
- Containers: Docker, Kubernetes
- Databases: PostgreSQL, MongoDB, Redis
- Tools: Git, Jenkins, Terraform

Certifications:
- AWS Certified Solutions Architect
- Certified Kubernetes Administrator (CKA)
`;

      try {
        console.log(`[Admin Panel] Testing provider: ${provider.displayName} with model: ${provider.provider.model}`);
        const result = await provider.provider.analyzeCV(testCV);

        if (result.error) {
          showMessage(`❌ Test failed: ${result.error}`, 'error');
          console.error(`[Admin Panel] Provider ${providerName} test error:`, result);
        } else {
          const modelName = result.model || provider.provider.model || 'unknown';
          const duration = result.duration ? `(${result.duration}ms)` : '';
          showMessage(`✅ Test successful! Model: ${modelName} ${duration}`, 'success');
          console.log(`[Admin Panel] Provider ${providerName} test success:`, result);
        }
      } catch (testError) {
        // More detailed error messages
        let errorMsg = testError.message || testError.toString() || 'Unknown error';

        console.error(`[Admin Panel] Provider ${providerName} exception:`, testError);

        if (errorMsg.includes('CORS')) {
          errorMsg = 'CORS error: API calls from browser blocked. This is normal for some providers - production calls work from server-side.';
        } else if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
          errorMsg = 'Network error: Cannot reach AI provider. Check your internet connection and API URL.';
        } else if (errorMsg.includes('401') || errorMsg.includes('403') || errorMsg.includes('Unauthorized')) {
          errorMsg = 'Authentication error: Invalid API key or insufficient permissions. Please check your environment variables.';
        } else if (errorMsg.includes('429')) {
          errorMsg = 'Rate limit exceeded: Too many requests. Try again later.';
        } else if (errorMsg.includes('404')) {
          errorMsg = 'Not found: Check your API endpoint URL and model name. Model may not exist at this endpoint.';
        } else if (errorMsg.includes('replace is not a function')) {
          errorMsg = 'Internal error: Prompt template not properly configured. This has been fixed - please restart the application.';
        }

        showMessage(`❌ Test failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      showMessage(`Test failed: ${error.message}`, 'error');
    }
  };

  // Roadmap feature statuses
  const roadmapItems = [
    {
      key: 'provider_discovery',
      title: 'Provider discovery & health',
      desc: 'List healthy providers and basic test run',
      status: providers && providers.length > 0 ? 'Implemented' : 'Pending'
    },
    {
      key: 'supabase_config',
      title: 'Supabase config persistence',
      desc: 'Read/write ai_provider_config with priority/enable',
      status: configurations && configurations.length > 0 ? 'Implemented' : 'Pending'
    },
    {
      key: 'usage_stats',
      title: 'Usage statistics',
      desc: 'Requests, success/fail, tokens, cost by provider/model',
      status: statistics ? 'Implemented' : 'Pending'
    },
    {
      key: 'logs_cleanup',
      title: 'Logs cleanup',
      desc: 'Retention policy for ai_usage_logs',
      status: 'Planned'
    },
    {
      key: 'budgets_alerts',
      title: 'Budgets & alerts',
      desc: 'Monthly budget caps and alert thresholds per provider',
      status: 'Planned'
    },
    {
      key: 'ab_testing',
      title: 'A/B testing',
      desc: 'Compare models/providers for tasks and auto-select best',
      status: 'Planned'
    },
    {
      key: 'auto_fallback',
      title: 'Auto-fallback rules',
      desc: 'Fallback to secondary provider on error/latency spike',
      status: 'Planned'
    },
    {
      key: 'task_allowlist',
      title: 'Per-task model allowlist',
      desc: 'Restrict models used for specific tasks/workflows',
      status: 'Planned'
    },
    {
      key: 'rate_limits',
      title: 'Rate limiting',
      desc: 'Throttle requests per provider/model to control spend',
      status: 'Planned'
    },
    {
      key: 'rbac',
      title: 'RBAC & service role',
      desc: 'Admin-only writes; view-only for non-admin roles',
      status: 'Planned'
    }
  ];

  const StatusBadge = ({ status }) => {
    const s = String(status || '').toLowerCase();
    const cls = s.includes('implemented')
      ? 'bg-green-900/20 text-green-400 border border-green-800'
      : s.includes('pending')
        ? 'bg-gray-700 text-gray-400 border border-gray-600'
        : 'bg-yellow-900/20 text-yellow-400 border border-yellow-800';
    return <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${cls}`}>{status}</span>;
  };

  const openConfigModal = (providerName) => {
    const provider = providers.find(p => p.name === providerName);
    if (provider) {
      setSelectedProvider(provider);
      setConfigModalOpen(true);
    }
  };

  const closeConfigModal = () => {
    setConfigModalOpen(false);
    setSelectedProvider(null);
  };

  const handleSaveConfig = async (config) => {
    await saveConfiguration(config);
    await loadData(); // Reload to get updated config
  };

  const handleAddProvider = async (config) => {
    await saveConfiguration(config);
    await loadData(); // Reload to get updated config
    showMessage(`Provider "${config.display_name}" added successfully! Remember to add API key to .env file.`, 'success');
  };

  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const toggleProvider = async (providerName, enabled) => {
    const config = configurations.find(c => c.provider_name === providerName) || {
      provider_name: providerName,
      enabled,
      priority: configurations.length + 1
    };

    config.enabled = enabled;
    await saveConfiguration(config);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 font-mono">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">AI Model Management</h1>
        <p className="mt-2 text-sm text-gray-400">
          Configure and monitor AI providers for CV analysis and immigration assessments
        </p>
      </div>

      {/* Message Banner */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg border ${message.type === 'success' ? 'bg-green-900/20 text-green-400 border-green-800' :
            message.type === 'error' ? 'bg-red-900/20 text-red-400 border-red-800' :
              'bg-blue-900/20 text-blue-400 border-blue-800'
          }`}>
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('providers')}
            className={`${activeTab === 'providers'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Providers
          </button>
          <button
            onClick={() => setActiveTab('statistics')}
            className={`${activeTab === 'statistics'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Statistics
          </button>
          <button
            onClick={() => setActiveTab('configuration')}
            className={`${activeTab === 'configuration'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Configuration
          </button>
        </nav>
      </div>

      {/* Providers Tab */}
      {activeTab === 'providers' && (
        <div className="space-y-6">
          <div className="bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-700">
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-white">Available Providers</h2>
                <p className="mt-1 text-sm text-gray-400">
                  {providers.length} provider(s) configured and healthy
                </p>
              </div>
              <button
                onClick={() => setAddModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Provider
              </button>
            </div>

            <div className="divide-y divide-gray-700">
              {providers.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-400">
                  No healthy providers available. Please configure API keys in environment variables.
                </div>
              ) : (
                providers.map((provider) => (
                  <div key={provider.name} className="px-6 py-4 hover:bg-gray-700/50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h3 className="text-lg font-medium text-white">
                            {provider.displayName}
                          </h3>
                          <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/20 text-green-400 border border-green-800">
                            Healthy
                          </span>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <div>
                            <p className="text-sm text-gray-400">Provider</p>
                            <p className="text-sm font-medium text-white">{provider.name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-400">Pricing (per 1M tokens)</p>
                            <p className="text-sm font-medium text-white">
                              ${provider.pricing.inputCostPerMillion.toFixed(2)} / ${provider.pricing.outputCostPerMillion.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-400">Rate Limit</p>
                            <p className="text-sm font-medium text-white">
                              {provider.rateLimits.requestsPerMinute}/min
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                        <button
                          onClick={() => openConfigModal(provider.name)}
                          className="inline-flex items-center px-3 py-2 border border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Configure
                        </button>
                        <button
                          onClick={() => testProvider(provider.name)}
                          className="inline-flex items-center px-3 py-2 border border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Test
                        </button>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={configurations.find(c => c.provider_name === provider.name)?.enabled !== false}
                            onChange={(e) => toggleProvider(provider.name, e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Provider System Status */}
          {statistics && (
            <div className="bg-gray-800 shadow rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-medium text-white mb-4">Provider System Status</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="bg-green-900/20 rounded-lg p-4 border border-green-800">
                  <p className="text-sm text-green-400">Healthy Providers</p>
                  <p className="text-2xl font-bold text-green-400">{statistics.providers.healthyProviders}</p>
                </div>
                <div className="bg-red-900/20 rounded-lg p-4 border border-red-800">
                  <p className="text-sm text-red-400">Unhealthy Providers</p>
                  <p className="text-2xl font-bold text-red-400">{statistics.providers.unhealthyProviders}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                  <p className="text-sm text-gray-300">Total Providers</p>
                  <p className="text-2xl font-bold text-white">{statistics.providers.totalProviders}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'statistics' && statistics && (
        <div className="space-y-6">
          {/* Usage Statistics */}
          <div className="bg-gray-800 shadow rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-medium text-white mb-4">Usage Statistics</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-800">
                <p className="text-sm text-blue-400">Total Requests</p>
                <p className="text-2xl font-bold text-blue-400">{statistics.usage.totalRequests}</p>
              </div>
              <div className="bg-green-900/20 rounded-lg p-4 border border-green-800">
                <p className="text-sm text-green-400">Successful</p>
                <p className="text-2xl font-bold text-green-400">{statistics.usage.successfulRequests}</p>
              </div>
              <div className="bg-red-900/20 rounded-lg p-4 border border-red-800">
                <p className="text-sm text-red-400">Failed</p>
                <p className="text-2xl font-bold text-red-400">{statistics.usage.failedRequests}</p>
              </div>
              <div className="bg-purple-900/20 rounded-lg p-4 border border-purple-800">
                <p className="text-sm text-purple-400">Total Tokens</p>
                <p className="text-2xl font-bold text-purple-400">{statistics.usage.totalTokens.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Usage by Provider */}
          <div className="bg-gray-800 shadow rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-medium text-white mb-4">Usage by Provider</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Requests
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Successful
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Failed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Total Tokens
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {Object.entries(statistics.usage.byProvider).map(([provider, stats]) => (
                    <tr key={provider}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                        {provider}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {stats.requests}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                        {stats.successful}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-400">
                        {stats.failed}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {stats.totalTokens.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Usage by Task */}
          <div className="bg-gray-800 shadow rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-medium text-white mb-4">Usage by Task</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Task
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Requests
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Successful
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Failed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Total Tokens
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {Object.entries(statistics.usage.byTask).map(([task, stats]) => (
                    <tr key={task}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                        {task.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {stats.requests}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                        {stats.successful}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-400">
                        {stats.failed}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {stats.totalTokens.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Roadmap & Status */}
          <div className="bg-gray-800 shadow rounded-lg p-6 mt-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Feature Roadmap & Status</h3>
              <button
                onClick={loadData}
                className="px-3 py-1 text-sm border border-gray-600 rounded hover:bg-gray-700 text-gray-300"
              >Refresh</button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Feature</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {roadmapItems.map((item) => (
                    <tr key={item.key}>
                      <td className="px-4 py-2 text-sm text-white">{item.title}</td>
                      <td className="px-4 py-2 text-sm text-gray-400">{item.desc}</td>
                      <td className="px-4 py-2 text-sm">
                        <StatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-xs text-gray-400">
              Planned: budgets/alerts, A/B testing, fallback rules, task allowlists, rate limits, RBAC.
            </div>
          </div>
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'configuration' && (
        <div className="bg-gray-800 shadow rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-medium text-white mb-4">Provider Configuration</h3>
          <div className="prose max-w-none">
            <p className="text-sm text-gray-400 mb-4">
              To configure AI providers, add the following environment variables to your <code>.env</code> file:
            </p>

            <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-x-auto border border-gray-700 text-gray-300">
              <pre className="whitespace-pre-wrap">
                {`# Primary AI Provider
REACT_APP_AI_PRIMARY_PROVIDER=gemini

# Fallback Providers (comma-separated)
REACT_APP_AI_FALLBACK_PROVIDERS=openai,anthropic

# Gemini Configuration
REACT_APP_GEMINI_API_KEY=your_gemini_api_key_here
REACT_APP_GEMINI_MODEL=gemini-2.5-flash

# OpenAI Configuration
REACT_APP_OPENAI_API_KEY=your_openai_api_key_here
REACT_APP_OPENAI_MODEL=gpt-4o-mini
REACT_APP_OPENAI_BASE_URL=https://api.openai.com/v1

# Anthropic Configuration
REACT_APP_ANTHROPIC_API_KEY=your_anthropic_api_key_here
REACT_APP_ANTHROPIC_MODEL=claude-3-5-sonnet-20241022

# Azure OpenAI Configuration
REACT_APP_AZURE_OPENAI_KEY=your_azure_key_here
REACT_APP_AZURE_OPENAI_ENDPOINT=https://your-resource.openai.azure.com
REACT_APP_AZURE_OPENAI_DEPLOYMENT=gpt-4o
REACT_APP_AZURE_OPENAI_API_VERSION=2024-02-15-preview

# DeepSeek Configuration
REACT_APP_DEEPSEEK_API_KEY=your_deepseek_api_key_here
REACT_APP_DEEPSEEK_MODEL=deepseek-chat`}
              </pre>
            </div>

            <p className="text-sm text-gray-400 mt-4">
              After updating environment variables, restart the application for changes to take effect.
            </p>
          </div>
        </div>
      )}

      {/* Provider Configuration Modal */}
      {configModalOpen && (
        <ProviderConfigModal
          provider={selectedProvider}
          onSave={handleSaveConfig}
          onClose={closeConfigModal}
        />
      )}

      {/* Add Provider Modal */}
      {addModalOpen && (
        <AddProviderModal
          existingProviders={providers}
          onSave={handleAddProvider}
          onClose={() => setAddModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AIModelManagement;
